import Data from 'data';
import { GameVersion, Quality } from 'common';
import { extraMonsterList, monsterBossList, monsterUniqueList, terrorZoneList, uniqueMonsterAuras } from './monsterList';
import { gameQualityMap } from 'logic/binary';
import { itemAffixLevel, itemCheckMod, itemGetType, itemGetTypes } from 'logic/item';
import { itemUpdate } from 'logic/itemReducers';
import { profileCanEquip } from 'logic/stateReducers';

function listMonsters(area, prefix) {
  const list = new Set();
  function add(id) {
    if (!id || list.has(id)) return;
    list.add(id);
    const mon = Data.monsters[id];
    if (mon) {
      add(mon.spawn);
      add(mon.minion1);
      add(mon.minion2);
    }
  }
  const count = (prefix === 'mon' ? area.nummon : 10);
  for (let i = 1; i <= count; ++i) {
    add(area[prefix + i]);
  }
  if (extraMonsterList[area.id]) {
    for (const id of extraMonsterList[area.id]) {
      if (id === '_') {
        list.clear();
      } else {
        add(id);
      }
    }
  }
  return [...list];
}

export function monsterIsType(type, what) {
  if (type === what) return true;
  const info = Data.monType[type];
  return info && ((info.equiv1 && monsterIsType(info.equiv1, what)) ||
    (info.equiv2 && monsterIsType(info.equiv2, what)) || (info.equiv3 && monsterIsType(info.equiv3, what)));
}

export function monsterList(monster, difficulty) {
  const type = monster.type;
  if (type === 'normal' || type === 'champion' || type === 'unique') {
    return listMonsters(Data.levels[monster.area], difficulty ? 'nmon' : (type === 'unique' ? 'umon' : 'mon'));
  } else {
    return [];
  }
}

export function monsterMods(type, id, difficulty) {
  const monid = (type === 'super' ? Data.superUniques[id]?.class : id);
  const mondata = Data.monsters[monid];
  const montype = mondata?.montype;
  const result = [];
  for (const row of Object.values(Data.uniqueMods)) {
    if (row.exclude1 && monsterIsType(montype, row.exclude1)) continue;
    if (row.exclude2 && monsterIsType(montype, row.exclude1)) continue;
    if (row.fpick === 2 && mondata?.nomultishot) continue;
    if (row['upick' + ['', 'n', 'h'][difficulty]]) {
      result.push(row.uniquemod);
    }
  }
  return result;
}

export function monsterLevelBase(monster, difficulty, playerLevel) {
  const type = monster.type;
  if (type === 'dummy' || type === 'summon') return monster.level;
  const monData = Data.monsters[type === 'super' ? Data.superUniques[monster.id]?.class : monster.id];
  if (!monData) return 0;
  let level = monData['level' + ['', 'n', 'h'][difficulty]] || 0;
  if ((!monData.boss && difficulty) || monster.terrorized) {
    let area = monster.area;
    if (type === 'super') {
      area = monsterUniqueList[monster.id]?.area;
    } else if (type === 'boss') {
      area = monsterBossList[monster.id]?.area;
    }
    const areaData = Data.levels[area];
    if (areaData) {
      level = areaData['monlvl' + (difficulty + 1) + (GameVersion === 100 ? 'ex' : '')];
    }
    if (monster.terrorized) {
      level = Math.max(level, Math.min([45, 71, 96][difficulty], (playerLevel ?? 99) + 2));
    }
  }
  return level;
}

export function monsterLevelBonus(monster) {
  const type = monster.type;
  if (type === 'champion') {
    return (monster.champion === 'berserk' ? 3 : 2);
  } else if (type === 'unique' || type === 'super') {
    return 3;
  } else if (type === 'boss') {
    if (monsterBossList[monster.id].unique || monster.terrorized) {
      return 3;
    }
  } else if (type === 'normal' && monster.minion) {
    return 3;
  }
  return 0;
}

export function monsterLevel(monster, difficulty, playerLevel) {
  return monsterLevelBase(monster, difficulty, playerLevel) + monsterLevelBonus(monster);
}

export function monsterHealth(monster, difficulty, playerLevel) {
  const suffix = ['', 'n', 'h'][difficulty];
  if (monster.type === 'dummy') {
    const monLvl = Data.monLvl[monster.level];
    const value = monLvl[(Data.info.pd2 || monster.single ? 'l' : '') + 'hp' + suffix];
    return {min: value, max: value};
  }
  const info = Data.monsters[monster.type === 'super' ? Data.superUniques[monster.id]?.class : monster.id];
  if (!info) return null;
  let min = info['minhp' + suffix];
  let max = info['maxhp' + suffix];
  const monLvl = !info.noratio && Data.monLvl[monsterLevelBase(monster, difficulty, playerLevel)];
  if (monLvl) {
    const mult = monLvl[(Data.info.pd2 || monster.single ? 'l' : '') + 'hp' + suffix];
    min = (mult * min / 100) | 0;
    max = (mult * max / 100) | 0;
  }
  return {min, max};
}

export function monsterAttacks(monster, difficulty) {
  if (monster.type === 'dummy') return ['a1'];
  const info = Data.monsters[monster.type === 'super' ? Data.superUniques[monster.id]?.class : monster.id];
  if (!info) return null;
  const result = [];
  const suffix = ['', 'n', 'h'][difficulty];
  if (info.ma1 && info['a1maxd' + suffix]) result.push('a1');
  if (info.ma2 && info['a2maxd' + suffix]) result.push('a2');
  if (info.ms1 && info['s1maxd' + suffix]) result.push('s1');
  return result;
}

export function monsterItems(id, level) {
  const rows = Data.monEquip.filter(row => row.monster === id && row.level <= level);
  if (!rows?.length) return null;
  const slots = {}, locked = {};
  function pushLoc(loc, item, mod) {
    if (item) {
      slots[loc].push([item, mod]);
    } else {
      for (const base of Object.keys(Data.armor)) {
        if (profileCanEquip({class: 'ass'}, loc, {base})) {
          slots[loc].push([base, mod]);
        }
      }
    }
  }
  for (const row of rows) {
    if (!row.loc1) continue;
    if ((!row.loc2 || row.loc2 === row.loc1) && (!row.loc3 || row.loc3 === row.loc1)) {
      if (!locked[row.loc1]) {
        slots[row.loc1] = [];
        pushLoc(row.loc1, row.item1, row.mod1);
        if (row.loc2) pushLoc(row.loc1, row.item2, row.mod2);
        if (row.loc3) pushLoc(row.loc1, row.item3, row.mod3);
        locked[row.loc1] = true;
      }
    } else {
      if (row.loc1 && !locked[row.loc1]) {
        slots[row.loc1] = slots[row.loc1] || [];
        pushLoc(row.loc1, row.item1, row.mod1);
      }
      if (row.loc2 && !locked[row.loc2]) {
        slots[row.loc2] = slots[row.loc2] || [];
        pushLoc(row.loc2, row.item2, row.mod2);
      }
      if (row.loc3 && !locked[row.loc3]) {
        slots[row.loc3] = slots[row.loc3] || [];
        pushLoc(row.loc3, row.item3, row.mod3);
      }
    }
  }
  return slots;
}

const randval = (x, y) => (x < y ? x + Math.floor(Math.random() * (y - x + 1)) : y);
function randomValues(mod) {
  const result = [];
  if (mod.mod1code) result[0] = randval(mod.mod1min, mod.mod1max);
  if (mod.mod2code) result[1] = randval(mod.mod2min, mod.mod2max);
  if (mod.mod3code) result[2] = randval(mod.mod3min, mod.mod3max);
  return result;
}

function randomMods(id, quality, ilvl) {
  const alvl = itemAffixLevel(id, ilvl);
  const types = itemGetTypes(id);
  const item = Data.items[id];
  function filter(mod) {
    if (alvl < mod.level || (mod.maxlevel != null && alvl > mod.maxlevel)) return false;
    return itemCheckMod(mod, item, types, quality);
  }
  const prefixes = Object.entries(Data.magicPrefix).filter(([, mod]) => filter(mod));
  const suffixes = Object.entries(Data.magicSuffix).filter(([, mod]) => filter(mod));

  const sumPrefixes = prefixes.reduce((sum, [, mod]) => sum + (mod.frequency || 0), 0);
  const sumSuffixes = suffixes.reduce((sum, [, mod]) => sum + (mod.frequency || 0), 0);
  function choose(list, sum) {
    let pos = Math.floor(Math.random() * sum);
    for (const item of list) {
      if (item[1].frequency > pos) return item;
      pos -= item[1].frequency;
    }
  }
  const groups = new Set();
  function gchoose(list, sum) {
    let result;
    for (let i = 0; i < 8; ++i) {
      result = choose(list, sum);
      if (result && !groups.has(result[1].group)) return result;
    }
  }
  const mods = {};
  function addmod(list, sum) {
    const mod = gchoose(list, sum);
    if (mod) {
      mods[mod[0]] = randomValues(mod[1]);
      groups.add(mod[1].group);
    }
  }

  if (quality === Quality.MAGIC) {
    if (Math.random() < 0.5) {
      addmod(prefixes, sumPrefixes);
      if (Math.random() < 0.5) {
        addmod(suffixes, sumSuffixes);
      }
    } else {
      addmod(suffixes, sumSuffixes);
    }
  } else {
    const count = 3 + Math.floor(Math.random() * 4);
    let numP = 0, numS = 0;
    for (let i = 0; i < count; ++i) {
      if (numS >= 3 || (numP < 3 && Math.random() < 0.5)) {
        addmod(prefixes, sumPrefixes);
        numP += 1;
      } else {
        addmod(suffixes, sumSuffixes);
        numS += 1;
      }
    }
  }
  return mods;
}

function randomSuperior(id) {
  const types = itemGetTypes(id);
  const type = itemGetType(types);
  if (!type) return {};
  const mods = Object.entries(Data.qualityItems).filter(([, mod]) => mod[type]);
  if (!mods.length) return {};
  const mod = mods[Math.floor(Math.random() * mods.length)];
  return {[mod[0]]: randomValues(mod[1])};
}

function checkItem(item, ilvl) {
  if (item.quality === Quality.MAGIC || item.quality === Quality.RARE) {
    if (!item.mods) return false;
    const alvl = itemAffixLevel(item.base, ilvl);
    const types = itemGetTypes(item.base);
    function filter(mod) {
      if (alvl < mod.level || (mod.maxlevel != null && alvl > mod.maxlevel)) return false;
      return itemCheckMod(mod, null, types, item.quality);
    }
    for (const id in item.mods) {
      const mod = Data.mods[id];
      if (!mod || !filter(mod)) return false;
    }
  } else if (item.quality === Quality.SUPERIOR) {
    if (!item.superior) return false;
  }
  return true;
}

export function monsterRandomItem(base, quality, ilvl) {
  const item = {base, ilvl, quality};
  if (quality === Quality.MAGIC || item.quality === Quality.RARE) {
    item.mods = randomMods(base, quality, ilvl);
  } else if (quality === Quality.SUPERIOR) {
    item.superior = randomSuperior(base);
  }
  const {minac, maxac} = Data.items[base] || {};
  if (maxac) item.defense = randval(minac, maxac);
  if (maxac && itemUpdate({...item}).stats?.item_armor_percent) {
    item.defense = maxac + 1;
  }
  return item;
}

export function monsterFixItems(monster, level, unitLevel) {
  if (!unitLevel) unitLevel = level;
  const monId = (monster.type === 'super' ? Data.superUniques[monster.id]?.class : monster.id);
  const items = Data.monsters[monId]?.inventory && monsterItems(monId, unitLevel);
  if (!items) {
    if (monster.items) delete monster.items;
    return monster;
  }

  let changed = false;
  const result = {};
  for (const slot in items) {
    const item = monster.items?.[slot];
    if (!item || !items[slot].find(([b, q]) => b === item.base && gameQualityMap[q] === item.quality)) {
      const [base, quality] = items[slot][Math.floor(Math.random() * items[slot].length)];
      changed = true;
      result[slot] = monsterRandomItem(base, gameQualityMap[quality], level);
    } else if (!checkItem(item, level)) {
      changed = true;
      result[slot] = monsterRandomItem(item.base, item.quality, level);
    } else if (item.ilvl !== level) {
      changed = true;
      result[slot] = {...item, ilvl: level};
    } else {
      result[slot] = item;
    }
  }
  if (changed || !monster.items || Object.keys(monster.items).length < Object.keys(result).length) {
    monster.items = result;
  }
  return monster;
}

export function monsterPresetMods(type, id) {
  if (type === 'super') {
    const uinfo = Data.superUniques[id];
    return [uinfo.mod1, uinfo.mod2, uinfo.mod3].map(id => Data.uniqueMods[id]?.uniquemod).filter(Boolean);
  } else if (type === 'boss') {
    const uinfo = monsterBossList[id];
    if (uinfo.unique) {
      const uinfo2 = Data.superUniques[uinfo.unique];
      return [uinfo2.mod1, uinfo2.mod2, uinfo2.mod3].map(id => Data.uniqueMods[id]?.uniquemod).filter(Boolean);
    } else {
      return uinfo.mods;
    }
  }
}

export function monsterFix(monster, difficulty, playerLevel) {
  const type = monster.type;
  let maxMods = 0;
  if (type === 'dummy') {
    if (!monster.level) monster.level = 99;
  } if (type === 'normal' || type === 'champion' || type === 'unique') {
    function filterArea(id) {
      if (monster.terrorized && !terrorZoneList.includes(parseInt(id))) return false;
      return monsterList({type: monster.type, area: id}, difficulty).length > 0;
    }
    if (!Data.levels[monster.area] || !filterArea(monster.area)) monster.area = Object.keys(Data.levels).find(filterArea);
    const mons = listMonsters(Data.levels[monster.area], difficulty ? 'nmon' : (type === 'normal' ? 'mon' : 'umon'));
    if (!mons.includes(monster.id)) {
      monster.id = mons[0];
    }
    if (type === 'champion' && !monster.champion) {
      monster.champion = 'champion';
    }
    if (type === 'unique') {
      maxMods = difficulty + 1;
    }
  } else if (type === 'super') {
    function validId(id) {
      const info = monsterUniqueList[id];
      if (!info) return false;
      if (monster.terrorized && !terrorZoneList.includes(info.area)) return false;
      return true;
    }
    if (!validId(monster.id)) {
      monster.id = Object.keys(monsterUniqueList).find(validId);
    }
    maxMods = difficulty;
  } else if (type === 'boss') {
    function validId(id) {
      const info = monsterBossList[id];
      if (!info) return false;
      if (monster.terrorized && !terrorZoneList.includes(info.area)) return false;
      return true;
    }
    if (!validId(monster.id)) {
      monster.id = Object.keys(monsterBossList).find(validId);
    }
    maxMods = (monsterBossList[monster.id]?.unique ? difficulty : 0);
  }
  const presetMods = monsterPresetMods(type, monster.id);
  const level = monsterLevel(monster, difficulty, playerLevel);
  if (type === 'unique' || type === 'super' || type === 'boss') {
    if (!monster.mods) monster.mods = [];
    const found = new Set();
    const allowed = monsterMods(monster.type, monster.id, difficulty);
    monster.mods = monster.mods.map(id => {
      if (found.has(id)) return null;
      if (presetMods?.includes(id)) return null;
      if (!allowed.includes(id)) return null;
      found.add(id);
      return id;
    });
    monster.mods.length = maxMods;
    if ((monster.mods.includes('aura') || presetMods?.includes('aura')) && !monster.aura) {
      if (!monster.aura || (uniqueMonsterAuras[monster.aura]?.min || 0) > level) {
        monster.aura = Object.keys(uniqueMonsterAuras)[0];
      }
    }
  }

  const health = monsterHealth(monster, difficulty, playerLevel);
  if (health && (!monster.maxhp || monster.maxhp < health.min || monster.maxhp > health.max)) {
    monster.maxhp = (health.min + health.max) >> 1;
  }
  const attacks = monsterAttacks(monster, difficulty);
  if (attacks && !attacks.includes(monster.attack)) {
    monster.attack = attacks[0];
  }

  monsterFixItems(monster, level);
  return monster;
}

export function selectMonster(profile) {
  if (profile.monster) {
    return profile.monster;
  } else {
    return monsterFix({type: 'dummy', level: profile.level}, profile.difficulty, profile.level);
  }
}

export function monsterSetType(monster, type, difficulty, playerLevel) {
  return monsterFix({...monster, type}, difficulty, playerLevel);
}

export function monsterSetArea(monster, area, difficulty, playerLevel) {
  return monsterFix({...monster, area}, difficulty, playerLevel);
}

export function monsterSetId(monster, id, difficulty, playerLevel) {
  return monsterFix({...monster, id}, difficulty, playerLevel);
}

export function monsterSetMod(monster, index, id, difficulty, playerLevel) {
  const mods = monster.mods?.slice() || [];
  mods[index] = id;
  return monsterFix({...monster, mods}, difficulty, playerLevel);
}
